import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { pageTransition } from "@/lib/utils";
import { listEmployees } from "@/api/employees";
import { listDepartments } from "@/api/organizations";
import {
  inviteAllUninvited,
  inviteEmployee,
  listInvitations,
  type InvitationRow,
  type InvitationState,
} from "@/api/invitations";
import { usePermissions } from "@/hooks/usePermissions";
import { useDebounce } from "@/hooks/useDebounce";
import { useToast } from "@/hooks/useToast";
import { PageHeader } from "@/components/layout/PageHeader";
import { ExportCsvButton } from "@/components/shared/ExportCsvButton";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { QueryErrorState } from "@/components/shared/ErrorBoundary";
import { SavedViewsMenu } from "@/components/shared/SavedViewsMenu";
import { useSavedViews } from "@/hooks/useSavedViews";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Plus, Search, Upload, GitBranch, Users, SearchX, Send } from "lucide-react";
import {
  STATUS_LABELS,
  EMPLOYMENT_TYPE_LABELS,
} from "@/lib/constants";
import type { EmployeeListItem } from "@/types/employee";
import type { PaginatedResponse } from "@/types/common";
import type { DepartmentFull } from "@/types/organization";

export function EmployeeListPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { addToast } = useToast();

  const [data, setData] = useState<PaginatedResponse<EmployeeListItem> | null>(null);
  const [departments, setDepartments] = useState<DepartmentFull[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Load error is tracked separately from data so the retry UI can
  // replace the table instead of the table rendering against a stale
  // ``data`` snapshot after a failed refetch.
  const [loadError, setLoadError] = useState<unknown>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [invitations, setInvitations] = useState<Record<string, InvitationRow>>({});
  const [invitingAll, setInvitingAll] = useState(false);
  const [invitingIds, setInvitingIds] = useState<Set<string>>(new Set());

  const debouncedSearch = useDebounce(search);

  // Saved-views bridge: snapshot the three filter dimensions so pre-sets
  // like "Active engineers" or "On-leave contractors" can be recalled
  // with a click. `search` is intentionally part of the snapshot — users
  // think of "my view" as the whole filter state, not just the dropdowns.
  const filterSnapshot = { search, deptFilter, statusFilter };
  const savedViews = useSavedViews<typeof filterSnapshot>({
    scope: "employees",
    filters: filterSnapshot,
    defaultFilters: { search: "", deptFilter: "", statusFilter: "" },
    onApply: (f) => {
      setSearch(f.search);
      setDeptFilter(f.deptFilter);
      setStatusFilter(f.statusFilter);
      setPage(1);
    },
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [employees, depts] = await Promise.all([
        listEmployees(page, 20, {
          search: debouncedSearch || undefined,
          department_id: deptFilter || undefined,
          status: statusFilter as EmployeeListItem["status"] | undefined,
        }),
        departments.length === 0 ? listDepartments() : Promise.resolve(departments),
      ]);
      setData(employees);
      if (departments.length === 0) setDepartments(depts as DepartmentFull[]);
    } catch (err) {
      setLoadError(err);
      addToast("error", "Failed to load employees");
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, deptFilter, statusFilter, addToast, departments.length]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshInvitations = useCallback(async () => {
    if (!can("manage_employees")) return;
    try {
      const rows = await listInvitations();
      const map: Record<string, InvitationRow> = {};
      for (const r of rows) map[r.employee_id] = r;
      setInvitations(map);
    } catch {
      /* non-fatal — dot column just won't render */
    }
  }, [can]);

  useEffect(() => {
    refreshInvitations();
  }, [refreshInvitations]);

  const handleInviteAll = async () => {
    setInvitingAll(true);
    try {
      const res = await inviteAllUninvited();
      if (res.sent === 0) {
        addToast("info", "No uninvited employees to send to");
      } else {
        addToast("success", `Sent ${res.sent} invitation${res.sent === 1 ? "" : "s"}`);
      }
      await refreshInvitations();
    } catch {
      addToast("error", "Failed to send invitations");
    } finally {
      setInvitingAll(false);
    }
  };

  const handleInviteOne = async (employeeId: string) => {
    setInvitingIds((s) => new Set(s).add(employeeId));
    try {
      await inviteEmployee(employeeId);
      addToast("success", "Invitation sent");
      await refreshInvitations();
    } catch {
      addToast("error", "Failed to send invitation");
    } finally {
      setInvitingIds((s) => {
        const next = new Set(s);
        next.delete(employeeId);
        return next;
      });
    }
  };

  const STATE_DOT: Record<InvitationState, { cls: string; label: string }> = {
    active: { cls: "bg-success", label: "Active" },
    pending: { cls: "bg-warning", label: "Invited — pending" },
    expired: { cls: "bg-danger", label: "Invitation expired" },
    never_invited: { cls: "bg-border", label: "Not invited" },
  };

  const columns: Column<EmployeeListItem>[] = [
    {
      key: "name",
      header: "Name",
      render: (emp) => (
        <div className="flex items-center gap-3">
          <Avatar
            firstName={emp.first_name}
            lastName={emp.last_name}
            src={emp.avatar_url}
            size="sm"
          />
          <div>
            <p className="font-medium text-text-primary">
              {emp.first_name} {emp.last_name}
            </p>
            <p className="text-xs text-text-muted">{emp.work_email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "title",
      header: "Title",
      render: (emp) => (
        <span className="text-text-primary">{emp.title}</span>
      ),
    },
    {
      key: "department",
      header: "Department",
      render: (emp) => (
        <span className="text-text-muted">
          {emp.department?.name || "—"}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (emp) => (
        <span className="text-text-muted text-xs">
          {EMPLOYMENT_TYPE_LABELS[emp.employment_type]}
        </span>
      ),
      className: "hidden lg:table-cell",
    },
    ...(can("manage_employees")
      ? [
          {
            key: "invitation",
            header: "Account",
            render: (emp: EmployeeListItem) => {
              const inv = invitations[emp.id];
              const state: InvitationState = inv?.state ?? "never_invited";
              const dot = STATE_DOT[state];
              const canResend = state !== "active";
              const busy = invitingIds.has(emp.id);
              return (
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 rounded-full ${dot.cls}`}
                    title={dot.label}
                  />
                  <span className="text-xs text-text-muted">{dot.label}</span>
                  {canResend && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInviteOne(emp.id);
                      }}
                      className="ml-auto text-xs text-primary hover:underline disabled:opacity-50"
                    >
                      {busy
                        ? "Sending…"
                        : state === "never_invited"
                        ? "Invite"
                        : "Resend"}
                    </button>
                  )}
                </div>
              );
            },
            className: "hidden md:table-cell",
          } as Column<EmployeeListItem>,
        ]
      : []),
    {
      key: "status",
      header: "Status",
      render: (emp) => (
        <Badge
          variant={
            emp.status === "ACTIVE"
              ? "success"
              : emp.status === "ON_LEAVE"
              ? "warning"
              : emp.status === "TERMINATED"
              ? "danger"
              : "default"
          }
        >
          {STATUS_LABELS[emp.status]}
        </Badge>
      ),
    },
  ];

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="People"
        subtitle={`${data?.total ?? 0} employees`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/employees/org-chart")}
            >
              <GitBranch className="h-4 w-4" />
              Org chart
            </Button>
            {can("manage_employees") && (
              <ExportCsvButton resource="employees" />
            )}
            {can("manage_employees") && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleInviteAll}
                  isLoading={invitingAll}
                  title="Send invitations to every employee who has never been invited"
                >
                  <Send className="h-4 w-4" />
                  Invite All
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate("/employees/import")}
                >
                  <Upload className="h-4 w-4" />
                  Import
                </Button>
                <Button onClick={() => navigate("/employees/new")}>
                  <Plus className="h-4 w-4" />
                  Add Employee
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 max-w-sm">
          <Input
            placeholder="Search employees..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            icon={<Search className="h-4 w-4" />}
          />
        </div>
        <Select
          value={deptFilter}
          onChange={(e) => {
            setDeptFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "", label: "All Departments" },
            ...departments.map((d) => ({ value: d.id, label: d.name })),
          ]}
        />
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "", label: "All Statuses" },
            ...Object.entries(STATUS_LABELS).map(([v, l]) => ({
              value: v,
              label: l,
            })),
          ]}
        />
        <div className="ml-auto">
          <SavedViewsMenu
            views={savedViews.views}
            activeView={savedViews.activeView}
            hasUnsavedChanges={savedViews.hasUnsavedChanges}
            isDefault={savedViews.isDefault}
            onApply={savedViews.applyView}
            onSave={savedViews.saveView}
            onDelete={savedViews.deleteView}
            onRename={savedViews.renameView}
            onUpdateActive={savedViews.updateActiveView}
            onClear={savedViews.clear}
          />
        </div>
      </div>

      {/* Table — swap for a retry panel on a failed fetch. Showing an
          empty DataTable after an API 500 would be a silent lie; the
          QueryErrorState tells the user the request failed AND offers
          the same "Retry" the boundary would for a render crash. */}
      <div className="bg-surface rounded-xl border border-border">
        {loadError && !isLoading ? (
          <QueryErrorState
            title="Couldn't load employees"
            onRetry={fetchData}
            isRetrying={isLoading}
            error={loadError}
          />
        ) : (
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          keyExtractor={(emp) => emp.id}
          isLoading={isLoading}
          page={page}
          totalPages={data?.total_pages ?? 1}
          onPageChange={setPage}
          onRowClick={(emp) => navigate(`/employees/${emp.id}`)}
          emptyState={
            // When filters are set, "no results" is the story. Without
            // them, it's a blank roster — coach the admin to invite
            // their first person rather than stare at a dashed line.
            debouncedSearch || deptFilter || statusFilter ? (
              <EmptyState
                icon={SearchX}
                title="No matching employees"
                description="Try clearing a filter or adjusting your search."
                action={{
                  label: "Clear filters",
                  onClick: () => {
                    setSearch("");
                    setDeptFilter("");
                    setStatusFilter("");
                    setPage(1);
                  },
                }}
              />
            ) : can("manage_employees") ? (
              <EmptyState
                icon={Users}
                title="No employees yet"
                description="Add your first teammate or import a CSV to get your roster started."
                action={{ label: "Add Employee", to: "/employees/new" }}
                secondaryAction={{
                  label: "Import CSV",
                  to: "/employees/import",
                }}
              />
            ) : (
              <EmptyState
                icon={Users}
                title="No employees yet"
                description="Ask your HR admin to add teammates — you'll see the directory here once there's someone to see."
              />
            )
          }
        />
        )}
      </div>
    </motion.div>
  );
}
